/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import { Select } from 'antd'
import colors from '@sportninja/common/constants/appColors'
import Text from 'src/components/ts-components/Text'
import { typeOptions } from '../../constants/typeOptions'
import { typeDisplayLabels } from '../../constants/typeDisplayLabels'
import React from 'react'
import {
  questionTypeIconMap,
  DefaultQuestionIcon,
} from 'src/components/ts-components/QuestionTypeIcons'
import { Question } from './types'

/**
 * 🔄 QuestionTypeSelector: Custom grouped dropdown for question type selection
 *
 * This component displays question types in two categories (General first):
 * - General Fields: All standard question types
 * - Specialized Fields: player_position, player_number, hockey_usa, country, relationship
 *
 * Follows the same design pattern as TeamInfoSection's team dropdown
 */

interface QuestionTypeSelectorProps {
  /** Current selected question type value */
  value: string
  /** Callback when type selection changes */
  onChange: (value: string) => void
  /** Whether the selector is disabled */
  disabled?: boolean
  /** Field errors array for validation */
  fieldErrors?: string[]
  /** Custom error messages object */
  customError?: Record<string, string>
  /** Input name for form identification */
  name: string
  /** Placeholder text when no value selected */
  placeholder?: string
  /** Label text displayed above the selector */
  label?: string
}

// Define custom field types that should be grouped separately
const CUSTOM_FIELD_TYPES = [
  'player_position',
  'player_number',
  'hockey_usa',
  'hockey_canada',
  'country',
  'relationship',
  'teamOfficialRole',
]

/**
 * 🔄 getGroupedQuestionTypeOptions: Generates grouped options for the dropdown
 *
 * Separates typeOptions into Custom Fields and Default Fields groups
 * Returns formatted option groups for Ant Design Select component
 */
const getGroupedQuestionTypeOptions = () => {
  console.log('🔄 getGroupedQuestionTypeOptions: Generating grouped options')

  // Separate options into custom and default categories
  const customOptions = typeOptions.filter((option) =>
    CUSTOM_FIELD_TYPES.includes(option.value)
  )

  const defaultOptions = typeOptions.filter(
    (option) => !CUSTOM_FIELD_TYPES.includes(option.value)
  )

  // Create grouped options structure with explicit type
  const groupedOptions: Array<{
    label: React.ReactNode
    options: Array<{
      value: string
      label: React.ReactNode
    }>
  }> = []

  // Add General group first if there are any default options
  if (defaultOptions.length > 0) {
    groupedOptions.push({
      label: (
        <div
          css={css`
            padding: 4px 0;
          `}
        >
          <Text
            variant='paragraphSmall'
            weight='semiBold'
            color={colors.PRIMARY}
          >
            General Fields
          </Text>
        </div>
      ),
      options: defaultOptions.map((option) => {
        const IconComponent =
          questionTypeIconMap[option.value as Question['type']] ||
          DefaultQuestionIcon
        return {
          value: option.value,
          label: (
            <div
              css={css`
                display: flex;
                align-items: center;
                gap: 8px;
                padding: 4px 0;
              `}
            >
              <IconComponent size={20} color={colors.NEUTRAL_50} />
              <Text
                variant='paragraphSmall'
                weight='regular'
                color={colors.WHITE}
              >
                {
                  typeDisplayLabels[
                    option.value as keyof typeof typeDisplayLabels
                  ]
                }
              </Text>
            </div>
          ),
        }
      }),
    })
  }

  // Add Specialized group after General if there are any custom options
  if (customOptions.length > 0) {
    groupedOptions.push({
      label: (
        <div
          css={css`
            padding: 4px 0;
          `}
        >
          <Text
            variant='paragraphSmall'
            weight='semiBold'
            color={colors.PRIMARY}
          >
            Specialized Fields
          </Text>
        </div>
      ),
      options: customOptions.map((option) => {
        const IconComponent =
          questionTypeIconMap[option.value as Question['type']] ||
          DefaultQuestionIcon
        return {
          value: option.value,
          label: (
            <div
              css={css`
                display: flex;
                align-items: center;
                gap: 8px;
                padding: 4px 0;
              `}
            >
              <IconComponent size={20} color={colors.NEUTRAL_50} />
              <Text
                variant='paragraphSmall'
                weight='regular'
                color={colors.WHITE}
              >
                {
                  typeDisplayLabels[
                    option.value as keyof typeof typeDisplayLabels
                  ]
                }
              </Text>
            </div>
          ),
        }
      }),
    })
  }

  return groupedOptions
}

// Static styles to avoid re-creation on every render
const labelStyle = css`
  color: ${colors.WHITE};
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  margin-bottom: 4px;
  display: block;
`

const selectStyle = css`
  width: 100%;
  height: 40px;
  .ant-select-selection-item {
    display: flex;
    align-items: center;
  }
`

const selectWrapperStyle = css`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
`

const errorStyle = css`
  transition: opacity 0.1s ease-in-out;
  font-size: 13px;
  line-height: 16px;
  color: ${colors.ERROR_LIGHT};
  margin-top: 4px;
`

/**
 * 🔄 QuestionTypeSelector Component
 *
 * Provides a grouped dropdown for selecting question types with custom styling
 * and error handling. Follows the design pattern established in TeamInfoSection.
 */
const QuestionTypeSelector: React.FC<QuestionTypeSelectorProps> = ({
  value,
  onChange,
  disabled = false,
  fieldErrors = [],
  customError = {},
  name,
  placeholder = 'Select Question Type',
  label = 'Question Type',
}) => {
  console.log('🔄 QuestionTypeSelector: Rendering with value', { value, name })

  // Check if field has error
  const hasError = fieldErrors.includes(name)

  // Get grouped options
  const groupedOptions = getGroupedQuestionTypeOptions()

  return (
    <div css={selectWrapperStyle}>
      {label && <span css={labelStyle}>{label}</span>}
      <Select
        css={selectStyle}
        status={hasError ? 'error' : undefined}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        options={groupedOptions}
        disabled={disabled}
        showSearch={false}
      />
      {hasError && customError[name] && (
        <div
          css={css`
            ${errorStyle}
            opacity: 1;
          `}
        >
          <span>{customError[name]}</span>
        </div>
      )}
    </div>
  )
}

export default QuestionTypeSelector
